import React, { useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { CheckCircle, OpenInNew, TaskAlt } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { WarehouseAPI } from '../../services/api';
import { ActionableItem } from '../../types';
import { ActionableBody } from './renderers';
import { ageDays, ageLabel, formatIstTime } from './actionableUtils';
import { display } from './brand';

interface ActionableCardProps {
    item: ActionableItem;
    index: number;
    onDone: (item: ActionableItem) => void;
}

// One open actionable. "Mark done" never just closes the item: it asks the server to run the rule's
// live check (for Move back to Primary: is that version Active right now?) and only a pass closes it.
const ActionableCard: React.FC<ActionableCardProps> = ({ item, index, onDone }) => {
    const navigate = useNavigate();
    const reduceMotion = useReducedMotion();
    const [checking, setChecking] = useState(false);
    const [failure, setFailure] = useState<{ message: string; at: string } | null>(item.lastCheck && !item.lastCheck.ok ? { message: item.lastCheck.message, at: item.lastCheck.at } : null);
    const [succeeded, setSucceeded] = useState<string | null>(null);
    const [shake, setShake] = useState(0);
    const old = ageDays(item.firstSeenAt) >= 3;

    const markDone = async () => {
        setChecking(true);
        try {
            const res = await WarehouseAPI.markActionableDone(item._id);
            if (res.done) {
                setFailure(null);
                setSucceeded(res.message);
                setTimeout(() => onDone(item), reduceMotion ? 0 : 900);
            } else {
                setFailure({ message: res.message, at: new Date().toISOString() });
                setShake(s => s + 1);
            }
        } catch (err: any) {
            setFailure({ message: err?.response?.data?.message || 'Could not verify right now. Please try again.', at: new Date().toISOString() });
            setShake(s => s + 1);
        } finally {
            setChecking(false);
        }
    };

    return (
        <motion.div
            layout={!reduceMotion}
            initial={reduceMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.94 }}
            whileHover={reduceMotion ? undefined : { y: -3 }}
            transition={{ duration: 0.26, delay: Math.min(index, 10) * 0.04, ease: [0.16, 1, 0.3, 1] }}
            style={{ borderRadius: 16, height: '100%' }}
        >
            <Box
                sx={{
                    position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    bgcolor: '#fff', borderRadius: '16px', border: '1px solid #ececE4',
                    boxShadow: '0 1px 2px rgba(26,20,15,0.04), 0 10px 28px rgba(26,20,15,0.06)', p: 2.25,
                    '&::before': { content: '""', position: 'absolute', left: 0, top: 0, right: 0, height: 3, bgcolor: succeeded ? '#16a34a' : old ? '#dc2626' : '#DC5829' },
                    opacity: succeeded ? 0.8 : 1, transition: 'opacity 200ms, box-shadow 200ms',
                    '&:hover': { boxShadow: '0 2px 4px rgba(26,20,15,0.05), 0 16px 36px rgba(220,88,41,0.12)' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap', mb: 1.75 }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ ...display('1.15rem', '-0.02em'), lineHeight: 1.25 }}>{item.title}</Typography>
                        {item.ruleKey !== 'pending_access_request' && (
                            <Typography sx={{ fontSize: '0.74rem', color: 'text.secondary', fontFamily: 'monospace', fontWeight: 600 }}>{item.subjectKey}</Typography>
                        )}
                    </Box>
                    <Tooltip title={`First flagged ${new Date(item.firstSeenAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}>
                        <Chip size="small" label={ageLabel(item.firstSeenAt)} sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: old ? 'rgba(220, 38, 38, 0.1)' : 'rgba(26,20,15,0.06)', color: old ? '#b91c1c' : 'text.secondary' }} />
                    </Tooltip>
                </Box>

                <ActionableBody item={item} />

                <Box sx={{ flex: 1 }} />
                <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed #e6e0d4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box component="ol" sx={{ display: 'flex', alignItems: 'center', gap: 1.25, m: 0, p: 0, listStyle: 'none', fontSize: '0.7rem', color: 'text.secondary', fontWeight: 600 }}>
                        {['Open the parent', 'Make it live', 'Mark done'].map((t, i) => (
                            <Box component="li" key={t} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: i === 2 ? '#B8451E' : undefined }}>
                                <Box component="span" sx={{ width: 17, height: 17, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.62rem', fontWeight: 800, bgcolor: i === 2 ? 'rgba(220,88,41,0.14)' : 'rgba(26,20,15,0.07)' }}>{i + 1}</Box>
                                {t}
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {item.link && (
                            <Button size="small" variant="outlined" startIcon={<OpenInNew sx={{ fontSize: 16 }} />} onClick={() => navigate(item.link!.path)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '999px', minHeight: 38, borderColor: '#d9d3c7', color: '#1A140F', '&:hover': { borderColor: '#DC5829', color: '#B8451E' } }}>
                                {item.link.label}
                            </Button>
                        )}
                        <Tooltip title="We check the version is live before closing this" arrow>
                            <span>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disableElevation
                                    disabled={checking || !!succeeded}
                                    onClick={markDone}
                                    startIcon={checking ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : succeeded ? <CheckCircle sx={{ fontSize: 16 }} /> : <TaskAlt sx={{ fontSize: 16 }} />}
                                    sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '999px', minHeight: 38, px: 2.25, bgcolor: succeeded ? '#16a34a' : '#DC5829', '&:hover': { bgcolor: '#B8451E' }, '&.Mui-disabled': { bgcolor: succeeded ? '#16a34a' : 'rgba(220,88,41,0.55)', color: '#fff' } }}
                                >
                                    {checking ? 'Checking…' : succeeded ? 'Done' : 'Mark done'}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>

                <Box aria-live="polite">
                    {failure && !succeeded && (
                        <motion.div
                            key={shake}
                            initial={reduceMotion ? false : { opacity: 0, x: -8 }}
                            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, x: [0, -6, 6, -4, 4, 0] }}
                            transition={{ duration: 0.4 }}
                        >
                            <Alert severity="warning" sx={{ mt: 1.5, borderRadius: '10px', alignItems: 'center', '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                                {failure.message} <Box component="span" sx={{ opacity: 0.7 }}> · checked {formatIstTime(failure.at)}</Box>
                            </Alert>
                        </motion.div>
                    )}
                    {succeeded && (
                        <motion.div initial={reduceMotion ? false : { opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                            <Alert severity="success" sx={{ mt: 1.5, borderRadius: '10px', '& .MuiAlert-message': { fontSize: '0.8rem' } }}>{succeeded} Marked done.</Alert>
                        </motion.div>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
};

export default ActionableCard;
